import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { firestoreService } from '@/services/firestoreService';

interface Ranking {
  id: string;
  type: 'company' | 'ceo' | 'polarizing' | 'competitive' | 'influential';
  rank: number;
  name: string;
  score: number;
  change?: number;
  companyId?: string;
  ticker?: string;
  logo?: string;
  date: any;
}

export const Rankings: React.FC = () => {
  const [rankings, setRankings] = useState<{
    polarizing: Ranking[];
    influential: Ranking[];
    competitive: Ranking[];
  }>({
    polarizing: [],
    influential: [],
    competitive: [],
  });
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<'polarizing' | 'influential' | 'competitive'>('polarizing');

  useEffect(() => {
    loadRankings();
  }, []);

  const loadRankings = async () => {
    try {
      setLoading(true);
      
      const [polarizing, influential, competitive] = await Promise.all([
        firestoreService.getDocuments<Ranking>('rankings', [
          firestoreService.where('type', '==', 'polarizing'),
          firestoreService.orderBy('score', 'desc'),
          firestoreService.limit(20),
        ]),
        firestoreService.getDocuments<Ranking>('rankings', [
          firestoreService.where('type', '==', 'influential'),
          firestoreService.orderBy('score', 'desc'),
          firestoreService.limit(20),
        ]),
        firestoreService.getDocuments<Ranking>('rankings', [
          firestoreService.where('type', '==', 'competitive'),
          firestoreService.orderBy('score', 'desc'),
          firestoreService.limit(20),
        ]),
      ]);

      setRankings({
        polarizing: polarizing.map((r, i) => ({ ...r, rank: i + 1 })),
        influential: influential.map((r, i) => ({ ...r, rank: i + 1 })),
        competitive: competitive.map((r, i) => ({ ...r, rank: i + 1 })),
      });
    } catch (error) {
      console.error('Failed to load rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentRankings = rankings[selectedType];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Rankings</h1>
          <p className="body-text text-gray-600">
            AI-powered rankings of companies and executives
          </p>
        </div>

        <div className="flex gap-4 mb-6">
          <Button
            variant={selectedType === 'polarizing' ? 'primary' : 'outline'}
            onClick={() => setSelectedType('polarizing')}
          >
            Polarizing Companies
          </Button>
          <Button
            variant={selectedType === 'influential' ? 'primary' : 'outline'}
            onClick={() => setSelectedType('influential')}
          >
            Influential CEOs
          </Button>
          <Button
            variant={selectedType === 'competitive' ? 'primary' : 'outline'}
            onClick={() => setSelectedType('competitive')}
          >
            Competitive Companies
          </Button>
        </div>

        {currentRankings.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600">
                No rankings available yet. Rankings are updated daily.
              </p>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 sub-header">Rank</th>
                    <th className="text-left py-3 px-4 sub-header">Name</th>
                    <th className="text-right py-3 px-4 sub-header">Score</th>
                    <th className="text-right py-3 px-4 sub-header">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRankings.map((ranking) => (
                    <tr key={ranking.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 body-text font-mono">{ranking.rank}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {ranking.logo && (
                            <img src={ranking.logo} alt={ranking.name} className="w-8 h-8 rounded" />
                          )}
                          <div>
                            <p className="body-text font-medium">{ranking.name}</p>
                            {ranking.ticker && (
                              <p className="body-text text-sm text-gray-500 font-mono">{ranking.ticker}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right body-text font-medium">
                        {ranking.score.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {ranking.change !== undefined && (
                          <span
                            className={`body-text ${
                              ranking.change >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {ranking.change >= 0 ? '+' : ''}
                            {ranking.change}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

